import { useEffect } from "react";
import {
  FieldValues,
  Path,
  UseFormGetValues,
  UseFormSetValue,
  UseFormWatch,
} from "react-hook-form";

import { IAttributeFieldValue } from "@interfaces/attribute.interface";

import { getAttributeValueUpdates } from "../utils";

interface UseCommonAttributeSyncOptions<T extends FieldValues> {
  watch: UseFormWatch<T>;
  getValues: UseFormGetValues<T>;
  setValue: UseFormSetValue<T>;
  // Field array holding the company's attribute values.
  companyName: Path<T>;
  // Field array holding the deal's attribute values.
  dealName: Path<T>;
  // Attributes that belong to both a company and its deal.
  commonAttributeIds: Set<string>;
  enabled: boolean;
}

// Keeps a company's and a deal's shared attributes in step within one form, both ways.
export function useCommonAttributeSync<T extends FieldValues>({
  watch,
  getValues,
  setValue,
  companyName,
  dealName,
  commonAttributeIds,
  enabled,
}: UseCommonAttributeSyncOptions<T>) {
  useEffect(() => {
    if (!enabled) return;

    const mirror = (sourceName: Path<T>, targetName: Path<T>) => {
      const source = getValues(sourceName) as IAttributeFieldValue[] | undefined;
      const target = getValues(targetName) as IAttributeFieldValue[] | undefined;
      if (!source?.length || !target?.length) return;

      const updates = getAttributeValueUpdates(source, target, commonAttributeIds);
      if (!updates.length) return;

      // Whole entries, on the path each field is registered under — the array and leaf paths
      // don't refresh a field the user has edited. Spreading carries `relations` through.
      updates.forEach(({ index, ...patch }) =>
        setValue(`${targetName}.${index}` as Path<T>, { ...target[index], ...patch } as never, {
          shouldDirty: true,
        })
      );
    };

    // The company is seeded before this effect runs, so that write is already gone.
    mirror(companyName, dealName);

    // The changed field's name is what tells the two directions apart.
    const subscription = watch((_values, { name }) => {
      if (!name) return;

      // A write to the array itself is a seed, and the company wins those.
      if (name === companyName || name === dealName) mirror(companyName, dealName);
      else if (name.startsWith(`${companyName}.`)) mirror(companyName, dealName);
      else if (name.startsWith(`${dealName}.`)) mirror(dealName, companyName);
    });

    return () => subscription.unsubscribe();
  }, [enabled, watch, getValues, setValue, companyName, dealName, commonAttributeIds]);
}
